import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocket } from '../hooks/useSocket';
import { useIsConnected } from '../stores/gameStore';

const LandingView = () => {
  const [name, setName] = useState(localStorage.getItem('yaq_player_name') || '');
  const [roomCode, setRoomCode] = useState('');
  const [penalty, setPenalty] = useState(localStorage.getItem('yaq_last_penalty') || '');
  const [showRejoinPrompt, setShowRejoinPrompt] = useState(false);
  const [savedRoom, setSavedRoom] = useState<string | null>(null);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  const reconnectAttempted = useRef(false);

  const isConnected = useIsConnected();
  const { createRoom, joinRoom, reconnectToRoom, clearSession } = useSocket();

  // Check for existing session on mount and auto-reconnect when connected
  useEffect(() => {
    // Only attempt once
    if (reconnectAttempted.current) return;

    const savedRoomCode = localStorage.getItem('youraiquiz-session');
    if (savedRoomCode) {
      try {
        const parsed = JSON.parse(savedRoomCode);
        if (parsed?.state?.gameState?.roomCode && parsed?.state?.clientState?.sessionToken) {
          setSavedRoom(parsed.state.gameState.roomCode);
          // Only show prompt if not yet connected - otherwise auto-reconnect
          if (isConnected) {
            reconnectAttempted.current = true;
            // Auto-attempt reconnection - if it fails, error handler will clear session
            reconnectToRoom();
          } else {
            setShowRejoinPrompt(true);
          }
        }
      } catch {
        // Invalid session data
        reconnectAttempted.current = true;
      }
    } else {
      reconnectAttempted.current = true;
    }
  }, [isConnected, reconnectToRoom]);

  const handleRejoin = () => {
    setShowRejoinPrompt(false);
    reconnectToRoom();
  };

  const handleDeclineClick = () => {
    if (confirmingDecline) {
      // Second click - actually decline
      setShowRejoinPrompt(false);
      setSavedRoom(null);
      setConfirmingDecline(false);
      clearSession();
    } else {
      // First click - show confirmation
      setConfirmingDecline(true);
      // Auto-reset after 3 seconds
      setTimeout(() => setConfirmingDecline(false), 3000);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !penalty.trim()) return;

    // Save to localStorage for next time
    localStorage.setItem('yaq_player_name', name.trim());
    localStorage.setItem('yaq_last_penalty', penalty.trim());

    if (roomCode.trim()) {
      joinRoom(roomCode.trim(), name.trim(), penalty.trim());
    } else {
      createRoom(name.trim(), penalty.trim());
    }
  };

  const inputClasses =
    'w-full bg-white/5 backdrop-blur-sm border-2 border-white/20 p-5 rounded-2xl focus:border-neon-purple focus:ring-4 focus:ring-neon-purple/20 outline-none transition-all text-xl font-black placeholder:text-white/20 text-white shadow-xl';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-md h-full flex flex-col justify-between py-12 px-4 relative z-10"
    >
      {/* Background Image Container */}
      <div className="fixed inset-0 z-[-1] overflow-hidden">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.85 }}
          className="w-full h-full bg-gradient-to-br from-purple-900/40 via-black to-blue-900/40"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/90" />
      </div>

      {/* Rejoin Prompt */}
      <AnimatePresence>
        {showRejoinPrompt && savedRoom && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              className="glass p-8 rounded-3xl max-w-sm w-full text-center space-y-6 border-2 border-neon-purple/30"
            >
              <div className="space-y-2">
                <h2 className="text-2xl font-black uppercase tracking-tight">Welcome Back!</h2>
                <p className="text-white/60 text-sm">
                  You were in a game. Want to rejoin?
                </p>
              </div>

              <div className="bg-white/10 rounded-2xl p-4">
                <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Room Code</p>
                <p className="text-4xl font-black text-neon-blue tracking-wider">{savedRoom}</p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleRejoin}
                  className="w-full bg-neon-purple text-white py-4 rounded-2xl font-black text-lg hover:scale-105 active:scale-95 transition-all"
                >
                  REJOIN GAME
                </button>
                <button
                  onClick={handleDeclineClick}
                  className={`w-full py-3 rounded-2xl font-bold text-sm transition-all ${
                    confirmingDecline
                      ? 'bg-red-600 text-white animate-pulse'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {confirmingDecline ? 'Tap again to confirm' : 'No thanks, start fresh'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="text-center space-y-2 mb-8 relative">
        <motion.h1
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-7xl font-black italic tracking-tighter uppercase leading-[0.8] drop-shadow-[0_10px_10px_rgba(0,0,0,0.9)]"
        >
          YOUR AI
          <br />
          <span className="text-neon-purple">QUIZ</span>
        </motion.h1>
        <div className="flex items-center justify-center gap-2 mt-4">
          <div
            className={`w-2 h-2 rounded-full ${
              isConnected
                ? 'bg-green-500 shadow-[0_0_10px_#22c55e]'
                : 'bg-red-500 shadow-[0_0_10px_#ef4444] animate-pulse'
            }`}
          />
          <span className="text-[8px] font-black uppercase tracking-[0.3em] text-white/70 bg-black/40 px-2 py-1 rounded-full backdrop-blur-md border border-white/10">
            Server: {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
        <div className="space-y-4">
          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase ml-2 text-white/60 tracking-widest drop-shadow-md">
              Your Nickname
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClasses}
              placeholder="e.g. BeerKing"
              maxLength={20}
              required
              autoComplete="off"
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase ml-2 text-white/60 tracking-widest drop-shadow-md">
              The Stakes
            </label>
            <input
              type="text"
              value={penalty}
              onChange={(e) => setPenalty(e.target.value)}
              className={inputClasses}
              placeholder="The loser must..."
              maxLength={40}
              required
              autoComplete="off"
            />
          </div>
        </div>

        <div className="pt-4 space-y-4">
          <div className="space-y-1">
            <label className="block text-[10px] font-black uppercase text-center text-white/50 tracking-widest drop-shadow-md">
              Join Private Party
            </label>
            <input
              type="text"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              className="w-full bg-white/5 backdrop-blur-sm border-2 border-dashed border-white/30 p-5 rounded-2xl text-center text-4xl font-black tracking-[0.4em] focus:border-neon-blue outline-none transition-all placeholder:text-white/10 text-white shadow-2xl"
              placeholder="CODE"
              maxLength={4}
              autoComplete="off"
            />
          </div>

          <button
            type="submit"
            disabled={!isConnected}
            className="group w-full bg-white text-black hover:bg-neon-purple hover:text-white p-6 rounded-2xl font-black text-2xl transition-all shadow-[0_15px_40px_rgba(0,0,0,0.6)] active:scale-95 flex items-center justify-center gap-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {roomCode.trim() ? 'JOIN PARTY' : 'START PRIVATE PARTY'}
          </button>
        </div>
      </form>

      {/* Footer */}
      <div className="mt-8 text-center relative z-10">
        <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">
          Submit penalties • Suggest categories • Vote out topics
        </p>
        <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest mt-1">
          Answer trivia • Loser spins the wheel of fate
        </p>
      </div>
    </motion.div>
  );
};

export default LandingView;
